import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useProjects } from "../context/ProjectContext";
import { hasRole } from "../lib/permissions";
import { BrandMark, BRAND } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import GanttChart from "../components/plan/GanttChart";
import ResourcesTab from "../components/plan/ResourcesTab";
import UsageTab from "../components/plan/UsageTab";
import BaselinesTab from "../components/plan/BaselinesTab";
import EvmTab from "../components/plan/EvmTab";
import PertChart from "../components/plan/PertChart";
import CalendarDialog from "../components/plan/CalendarDialog";
import ViewsBar from "../components/plan/ViewsBar";
import {
  ArrowLeft, FolderOpen, Plus, Trash, PencilSimple, ArrowsInLineHorizontal,
  ArrowsOutLineHorizontal, DownloadSimple, UploadSimple, Flag, MagnifyingGlassPlus,
  MagnifyingGlassMinus, CaretRight, CaretDown, Users, ChartBar, Camera, Wallet,
  Calendar as CalendarIcon, Graph, Stack,
} from "@phosphor-icons/react";

const ZOOM_OPTIONS = ["day", "week", "month"];
const CONSTRAINT_TYPES = [
  { value: "ASAP", label: "As Soon As Possible" },
  { value: "ALAP", label: "As Late As Possible" },
  { value: "MSO",  label: "Must Start On" },
  { value: "MFO",  label: "Must Finish On" },
  { value: "SNET", label: "Start No Earlier Than" },
  { value: "SNLT", label: "Start No Later Than" },
  { value: "FNET", label: "Finish No Earlier Than" },
  { value: "FNLT", label: "Finish No Later Than" },
];

function formatDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

// Left table row (recursive expand/collapse)
function TaskRow({
  t, depth, expanded, hasKids, isSelected, canEdit, showSlack,
  onToggle, onSelect, onEdit, onDelete, onIndent, onOutdent,
}) {
  const displayStart = t.computed_start || t.start;
  const displayFinish = t.computed_finish || t.finish;
  return (
    <tr
      onClick={() => onSelect(t)}
      data-testid={`plan-row-${t.id}`}
      className={`border-b border-neutral-100 cursor-pointer transition-colors ${
        isSelected ? "bg-sky-50" : "hover:bg-neutral-50"
      }`}
      style={{ height: 32 }}
    >
      <td className="px-2 text-[10px] font-mono text-neutral-500 w-16 whitespace-nowrap">
        {t.wbs || "—"}
      </td>
      <td className="pl-1 pr-2 text-sm text-neutral-800 truncate max-w-[280px]">
        <div className="flex items-center gap-1" style={{ paddingLeft: depth * 14 }}>
          {hasKids ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggle(t.id); }}
              className="h-4 w-4 flex items-center justify-center text-neutral-500 hover:text-neutral-900"
              data-testid={`plan-toggle-${t.id}`}
            >
              {expanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            </button>
          ) : <span className="w-4 inline-block" />}
          {t.is_milestone ? <Flag size={11} weight="fill" className="text-orange-500 shrink-0" /> : null}
          {t.is_critical && !hasKids ? (
            <span data-testid={`crit-dot-${t.id}`} className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" title="On critical path" />
          ) : null}
          <span className={hasKids ? "font-semibold" : ""}>{t.name}</span>
        </div>
      </td>
      <td className="px-2 text-xs text-neutral-600 whitespace-nowrap">{formatDate(displayStart)}</td>
      <td className="px-2 text-xs text-neutral-600 whitespace-nowrap">{formatDate(displayFinish)}</td>
      <td className="px-2 text-xs text-neutral-600 whitespace-nowrap text-right w-14">
        {t.is_milestone ? "—" : `${t.duration_days || 0}d`}
      </td>
      {showSlack ? (
        <td className={`px-2 text-xs whitespace-nowrap text-right w-16 font-mono ${
          t.total_slack === 0 && !hasKids ? "text-red-600 font-bold" : "text-neutral-500"
        }`} data-testid={`plan-slack-${t.id}`}>
          {t.total_slack == null ? "—" : `${t.total_slack}d`}
        </td>
      ) : null}
      <td className="px-2 text-xs text-neutral-600 whitespace-nowrap text-right w-14">
        {t.percent_complete || 0}%
      </td>
      <td className="px-2 whitespace-nowrap w-24">
        {canEdit ? (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
            <button type="button" onClick={(e) => { e.stopPropagation(); onIndent(t); }} className="h-6 w-6 flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded" title="Indent" data-testid={`plan-indent-${t.id}`}>
              <ArrowsInLineHorizontal size={12} weight="bold" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onOutdent(t); }} className="h-6 w-6 flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded" title="Outdent" data-testid={`plan-outdent-${t.id}`}>
              <ArrowsOutLineHorizontal size={12} weight="bold" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(t); }} className="h-6 w-6 flex items-center justify-center text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded" title="Edit" data-testid={`plan-edit-${t.id}`}>
              <PencilSimple size={12} weight="bold" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(t); }} className="h-6 w-6 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded" title="Delete" data-testid={`plan-delete-${t.id}`}>
              <Trash size={12} weight="bold" />
            </button>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

function TaskEditor({ open, onOpenChange, initial, tasks, resources, onSubmit }) {
  const [data, setData] = useState({
    name: "", start: "", finish: "", duration_days: "", percent_complete: 0,
    is_milestone: false, assignee: "", notes: "", predecessors: [],
    assignments: [], fixed_cost: 0, actual_cost: "",
  });
  useEffect(() => {
    if (initial) setData({
      name: initial.name || "",
      start: initial.start || "",
      finish: initial.finish || "",
      duration_days: initial.duration_days || "",
      percent_complete: initial.percent_complete || 0,
      is_milestone: !!initial.is_milestone,
      assignee: initial.assignee || "",
      notes: initial.notes || "",
      predecessors: initial.predecessors || [],
      assignments: initial.assignments || [],
      fixed_cost: initial.fixed_cost || 0,
      actual_cost: initial.actual_cost ?? "",
      constraint_type: initial.constraint_type || "ASAP",
      constraint_date: initial.constraint_date || "",
    });
    else setData({
      name: "", start: "", finish: "", duration_days: "", percent_complete: 0,
      is_milestone: false, assignee: "", notes: "", predecessors: [],
      assignments: [], fixed_cost: 0, actual_cost: "",
      constraint_type: "ASAP", constraint_date: "",
    });
  }, [initial, open]);

  const eligiblePreds = useMemo(
    () => (tasks || []).filter((t) => !initial || t.id !== initial.id),
    [tasks, initial],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...data,
      duration_days: data.duration_days === "" ? null : Number(data.duration_days),
      percent_complete: Number(data.percent_complete || 0),
      fixed_cost: Number(data.fixed_cost || 0),
      actual_cost: data.actual_cost === "" ? null : Number(data.actual_cost),
      constraint_type: data.constraint_type || "ASAP",
      constraint_date: data.constraint_date || null,
    };
    onSubmit(payload);
  };

  const togglePred = (id) => {
    setData((d) => {
      const has = (d.predecessors || []).some((p) => p.task_id === id);
      const next = has
        ? d.predecessors.filter((p) => p.task_id !== id)
        : [...(d.predecessors || []), { task_id: id, type: "FS", lag_days: 0 }];
      return { ...d, predecessors: next };
    });
  };

  const setPredField = (id, field, value) => {
    setData((d) => ({
      ...d,
      predecessors: (d.predecessors || []).map((p) =>
        p.task_id === id
          ? { ...p, [field]: field === "lag_days" ? Number(value || 0) : value }
          : p,
      ),
    }));
  };

  const toggleAssign = (rid) => {
    setData((d) => {
      const has = (d.assignments || []).some((a) => a.resource_id === rid);
      const next = has
        ? d.assignments.filter((a) => a.resource_id !== rid)
        : [...(d.assignments || []), { resource_id: rid, allocation_pct: 100 }];
      return { ...d, assignments: next };
    });
  };

  const setAssignPct = (rid, pct) => {
    setData((d) => ({
      ...d,
      assignments: (d.assignments || []).map((a) =>
        a.resource_id === rid ? { ...a, allocation_pct: Math.max(0, Number(pct) || 0) } : a,
      ),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="plan-task-editor">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit plan task" : "New plan task"}</DialogTitle>
          <DialogDescription>
            Set the schedule, progress and dependencies. Milestone tasks are single-day markers.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Task name</label>
            <Input
              autoFocus
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
              required
              data-testid="plan-editor-name"
              className="h-10 mt-1"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Start</label>
              <Input type="date" value={data.start || ""} onChange={(e) => setData({ ...data, start: e.target.value })} data-testid="plan-editor-start" className="h-10 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Finish</label>
              <Input type="date" value={data.finish || ""} onChange={(e) => setData({ ...data, finish: e.target.value })} data-testid="plan-editor-finish" className="h-10 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Duration (d)</label>
              <Input type="number" min="0" value={data.duration_days} onChange={(e) => setData({ ...data, duration_days: e.target.value })} data-testid="plan-editor-duration" className="h-10 mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">% Complete</label>
              <Input type="number" min="0" max="100" value={data.percent_complete} onChange={(e) => setData({ ...data, percent_complete: e.target.value })} data-testid="plan-editor-percent" className="h-10 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Assignee</label>
              <Input value={data.assignee} onChange={(e) => setData({ ...data, assignee: e.target.value })} data-testid="plan-editor-assignee" className="h-10 mt-1" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={data.is_milestone}
              onChange={(e) => setData({ ...data, is_milestone: e.target.checked })}
              data-testid="plan-editor-milestone"
            />
            Milestone (zero-duration marker)
          </label>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Predecessors</label>
            <div className="mt-1 max-h-40 overflow-y-auto border border-neutral-200 rounded p-2 space-y-1">
              {eligiblePreds.length === 0 ? (
                <div className="text-xs text-neutral-400">No other tasks yet</div>
              ) : eligiblePreds.map((t) => {
                const p = (data.predecessors || []).find((x) => x.task_id === t.id);
                const checked = !!p;
                return (
                  <div key={t.id} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={checked} onChange={() => togglePred(t.id)} data-testid={`editor-pred-toggle-${t.id}`} />
                    <span className="font-mono text-neutral-400 w-10">{t.wbs || "-"}</span>
                    <span className="flex-1 truncate">{t.name}</span>
                    {checked ? (
                      <>
                        <select
                          value={p.type || "FS"}
                          onChange={(e) => setPredField(t.id, "type", e.target.value)}
                          data-testid={`editor-pred-type-${t.id}`}
                          className="h-7 text-xs border border-neutral-200 rounded px-1 bg-white"
                        >
                          <option value="FS">FS</option>
                          <option value="SS">SS</option>
                          <option value="FF">FF</option>
                          <option value="SF">SF</option>
                        </select>
                        <input
                          type="number"
                          value={p.lag_days ?? 0}
                          onChange={(e) => setPredField(t.id, "lag_days", e.target.value)}
                          className="w-14 h-7 text-xs text-right border border-neutral-200 rounded px-1"
                          title="Lag days (negative = lead)"
                          data-testid={`editor-pred-lag-${t.id}`}
                        />
                        <span className="text-neutral-400">d</span>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Constraint</label>
              <select
                value={data.constraint_type || "ASAP"}
                onChange={(e) => setData({ ...data, constraint_type: e.target.value })}
                className="mt-1 h-10 w-full border border-neutral-200 rounded px-2 text-sm bg-white"
                data-testid="editor-constraint-type"
              >
                {CONSTRAINT_TYPES.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Constraint date</label>
              <Input
                type="date"
                value={data.constraint_date || ""}
                onChange={(e) => setData({ ...data, constraint_date: e.target.value })}
                disabled={!data.constraint_type || data.constraint_type === "ASAP" || data.constraint_type === "ALAP"}
                data-testid="editor-constraint-date"
                className="h-10 mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Resource assignments</label>
            <div className="mt-1 max-h-40 overflow-y-auto border border-neutral-200 rounded p-2 space-y-1" data-testid="editor-assignments">
              {(!resources || resources.length === 0) ? (
                <div className="text-xs text-neutral-400">Add resources on the Resources tab first.</div>
              ) : resources.map((r) => {
                const a = (data.assignments || []).find((x) => x.resource_id === r.id);
                return (
                  <div key={r.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!a}
                      onChange={() => toggleAssign(r.id)}
                      data-testid={`editor-assign-toggle-${r.id}`}
                    />
                    <span className="flex-1 truncate">{r.name} <span className="text-neutral-400">· {r.type}</span></span>
                    {a ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max="500"
                          value={a.allocation_pct}
                          onChange={(e) => setAssignPct(r.id, e.target.value)}
                          className="w-14 h-7 text-right border border-neutral-200 rounded px-1"
                          data-testid={`editor-assign-pct-${r.id}`}
                        />
                        <span className="text-neutral-400">%</span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Fixed cost ($)</label>
              <Input type="number" min="0" value={data.fixed_cost} onChange={(e) => setData({ ...data, fixed_cost: e.target.value })} className="h-10 mt-1" data-testid="editor-fixed-cost" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">Actual cost ($) so far</label>
              <Input type="number" min="0" value={data.actual_cost} onChange={(e) => setData({ ...data, actual_cost: e.target.value })} className="h-10 mt-1" data-testid="editor-actual-cost" placeholder="For EVM" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="text-white" style={{ backgroundColor: BRAND.colors.blue }} data-testid="plan-editor-save">
              {initial ? "Save changes" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Plan() {
  const { user } = useAuth();
  const { projects, projectId, setProjectId, activeProjects, project, customer } = useProjects();
  const canEdit = hasRole(user, "editor");
  const [tasks, setTasks] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState("day");
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);
  const [tab, setTab] = useState("gantt"); // gantt | pert | resources | usage | baselines | evm
  const [showCritical, setShowCritical] = useState(true);
  const [showSlack, setShowSlack] = useState(true);
  const [filterAssignee, setFilterAssignee] = useState("");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [levelingBusy, setLevelingBusy] = useState(false);
  // Undo/redo stacks — each entry: { taskId, before: {field: value...}, after: {field: value...} }
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const fileRef = useRef(null);

  const loadTasks = useCallback(async () => {
    if (!projectId) { setTasks([]); return; }
    setLoading(true);
    try {
      const [{ data: tData }, { data: rData }] = await Promise.all([
        api.get("/plan/tasks", { params: { project_id: projectId } }),
        api.get("/plan/resources", { params: { project_id: projectId } }),
      ]);
      setTasks(tData.tasks || []);
      setResources(rData.resources || []);
    } catch {
      setTasks([]); setResources([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Rows visible after applying collapse state + filters
  const visibleTasks = useMemo(() => {
    if (!tasks.length) return [];
    // Filter: assignee substring or hideCompleted (leaves only)
    const matches = (t) => {
      if (filterAssignee && !(t.assignee || "").toLowerCase().includes(filterAssignee.toLowerCase())) return false;
      if (hideCompleted && (t.percent_complete || 0) >= 100 && !tasks.some((x) => x.parent_id === t.id)) return false;
      return true;
    };
    // We keep a task if it or any descendant matches the filter, so parents don't disappear.
    const childrenMap = {};
    tasks.forEach((t) => { const p = t.parent_id || null; (childrenMap[p] = childrenMap[p] || []).push(t); });
    const hasMatchInSubtree = (t) => {
      if (matches(t)) return true;
      return (childrenMap[t.id] || []).some((c) => hasMatchInSubtree(c));
    };
    // parent -> children map preserving order
    const byParent = {};
    tasks.forEach((t) => {
      const pid = t.parent_id || null;
      (byParent[pid] = byParent[pid] || []).push(t);
    });
    const flat = [];
    const walk = (parentId, depth) => {
      (byParent[parentId] || []).forEach((t) => {
        if (!hasMatchInSubtree(t)) return;
        flat.push({ ...t, _depth: depth });
        if (collapsed.has(t.id)) return;
        walk(t.id, depth + 1);
      });
    };
    walk(null, 0);
    return flat;
  }, [tasks, collapsed, filterAssignee, hideCompleted]);

  const hasChildrenMap = useMemo(() => {
    const m = new Set();
    tasks.forEach((t) => { if (t.parent_id) m.add(t.parent_id); });
    return m;
  }, [tasks]);

  const openNew = () => { setEditorInitial(null); setEditorOpen(true); };
  const openEdit = (t) => { setEditorInitial(t); setEditorOpen(true); };

  const handleSave = async (payload) => {
    try {
      if (editorInitial) {
        // Snapshot only the fields being patched, so undo restores exactly what changed.
        const before = {};
        const after = {};
        Object.keys(payload).forEach((k) => {
          const oldVal = editorInitial[k];
          const newVal = payload[k];
          if (JSON.stringify(oldVal ?? null) !== JSON.stringify(newVal ?? null)) {
            before[k] = oldVal ?? null;
            after[k] = newVal;
          }
        });
        await api.patch(`/plan/tasks/${editorInitial.id}`, payload);
        if (Object.keys(after).length > 0) {
          setUndoStack((s) => [...s, { taskId: editorInitial.id, before, after }]);
          setRedoStack([]);
        }
        toast.success("Task updated");
      } else {
        await api.post("/plan/tasks", { project_id: projectId, ...payload });
        toast.success("Task created");
      }
      setEditorOpen(false);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.name}" and any children?`)) return;
    try {
      await api.delete(`/plan/tasks/${t.id}`);
      toast.success("Deleted");
      if (selectedId === t.id) setSelectedId(null);
      loadTasks();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleReschedule = async (t, { start, finish }) => {
    const before = { start: t.start, finish: t.finish };
    try {
      await api.patch(`/plan/tasks/${t.id}`, { start, finish });
      setUndoStack((s) => [...s, { taskId: t.id, before, after: { start, finish } }]);
      setRedoStack([]);
      toast.success(`Moved to ${start} → ${finish}`);
      loadTasks();
    } catch {
      toast.error("Could not reschedule");
    }
  };

  const handleResize = async (t, { start, finish }) => {
    const before = { start: t.start, finish: t.finish };
    try {
      await api.patch(`/plan/tasks/${t.id}`, { finish });
      setUndoStack((s) => [...s, { taskId: t.id, before, after: { start, finish } }]);
      setRedoStack([]);
      toast.success(`Resized · finish ${finish}`);
      loadTasks();
    } catch {
      toast.error("Could not resize");
    }
  };

  const handleConnect = async (fromTask, toTask) => {
    // Add a FS predecessor on toTask pointing to fromTask
    const currentPreds = toTask.predecessors || [];
    if (currentPreds.some((p) => p.task_id === fromTask.id)) {
      toast.info("Link already exists");
      return;
    }
    const before = { predecessors: currentPreds };
    const nextPreds = [...currentPreds, { task_id: fromTask.id, type: "FS", lag_days: 0 }];
    try {
      await api.patch(`/plan/tasks/${toTask.id}`, { predecessors: nextPreds });
      setUndoStack((s) => [...s, { taskId: toTask.id, before, after: { predecessors: nextPreds } }]);
      setRedoStack([]);
      toast.success(`Linked ${fromTask.name} → ${toTask.name}`);
      loadTasks();
    } catch {
      toast.error("Could not link");
    }
  };

  const doUndo = useCallback(async () => {
    if (undoStack.length === 0) return;
    const entry = undoStack[undoStack.length - 1];
    try {
      await api.patch(`/plan/tasks/${entry.taskId}`, entry.before);
      setUndoStack((s) => s.slice(0, -1));
      setRedoStack((s) => [...s, entry]);
      toast.success("Undone");
      loadTasks();
    } catch {
      toast.error("Undo failed");
    }
  }, [undoStack, loadTasks]);

  const doRedo = useCallback(async () => {
    if (redoStack.length === 0) return;
    const entry = redoStack[redoStack.length - 1];
    try {
      await api.patch(`/plan/tasks/${entry.taskId}`, entry.after);
      setRedoStack((s) => s.slice(0, -1));
      setUndoStack((s) => [...s, entry]);
      toast.success("Redone");
      loadTasks();
    } catch {
      toast.error("Redo failed");
    }
  }, [redoStack, loadTasks]);

  // Keyboard shortcuts: Cmd/Ctrl-Z (undo), Cmd/Ctrl-Shift-Z or Cmd/Ctrl-Y (redo).
  // Ignored while typing inside inputs/textareas.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if ((e.key === "z" || e.key === "Z") && !e.shiftKey) { e.preventDefault(); doUndo(); }
      else if (((e.key === "z" || e.key === "Z") && e.shiftKey) || e.key === "y" || e.key === "Y") { e.preventDefault(); doRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doUndo, doRedo]);

  const runReschedule = async () => {
    try {
      await api.post(`/plan/reschedule?project_id=${projectId}`);
      toast.success("Plan rescheduled");
      loadTasks();
    } catch {
      toast.error("Reschedule failed");
    }
  };

  const runLevelResources = async () => {
    if (levelingBusy) return;
    setLevelingBusy(true);
    try {
      const { data } = await api.post(`/plan/level-resources?project_id=${projectId}&apply=true`);
      if (data.shifted_count === 0) {
        toast.info(data.unresolved_count > 0
          ? `No slack available — ${data.unresolved_count} day(s) still over-allocated`
          : "No over-allocations found");
      } else {
        toast.success(`Leveled ${data.shifted_count} task${data.shifted_count === 1 ? "" : "s"}${data.unresolved_count > 0 ? `, ${data.unresolved_count} unresolved` : ""}`);
        // Push a bulk undo entry so the user can reverse leveling in one shot.
        setUndoStack((s) => [
          ...s,
          ...data.changed.map((c) => ({ taskId: c.id, before: c.before, after: c.after })),
        ]);
        setRedoStack([]);
      }
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Leveling failed");
    } finally {
      setLevelingBusy(false);
    }
  };

  // Views apply: writes the persisted config back into local state
  const applyViewConfig = (cfg) => {
    if (!cfg) return;
    if (cfg.zoom && ["day","week","month"].includes(cfg.zoom)) setZoom(cfg.zoom);
    if (typeof cfg.showCritical === "boolean") setShowCritical(cfg.showCritical);
    if (typeof cfg.showSlack === "boolean") setShowSlack(cfg.showSlack);
    if (typeof cfg.filterAssignee === "string") setFilterAssignee(cfg.filterAssignee);
    if (typeof cfg.hideCompleted === "boolean") setHideCompleted(cfg.hideCompleted);
  };
  const currentViewConfig = { zoom, showCritical, showSlack, filterAssignee, hideCompleted };

  // Indent: make previous sibling this task's parent.
  const handleIndent = async (t) => {
    // Find previous sibling within the same parent
    const siblings = tasks.filter((x) => x.parent_id === (t.parent_id || null))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = siblings.findIndex((x) => x.id === t.id);
    if (idx <= 0) { toast.error("Nothing to indent under"); return; }
    const newParent = siblings[idx - 1];
    try {
      await api.patch(`/plan/tasks/${t.id}`, { parent_id: newParent.id });
      loadTasks();
    } catch {
      toast.error("Indent failed");
    }
  };

  const handleOutdent = async (t) => {
    if (!t.parent_id) { toast.error("Already at top level"); return; }
    const parent = tasks.find((x) => x.id === t.parent_id);
    try {
      await api.patch(`/plan/tasks/${t.id}`, { parent_id: parent ? (parent.parent_id || null) : null });
      loadTasks();
    } catch {
      toast.error("Outdent failed");
    }
  };

  const handleToggleCollapse = (id) => {
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/plan/import?project_id=${projectId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} plan task${data.inserted === 1 ? "" : "s"}`);
      loadTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  const doExport = async (format) => {
    try {
      const res = await api.get("/plan/export", {
        params: { project_id: projectId, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project?.name || "plan")}_plan.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="plan-page">
      {/* Header */}
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Plan · Gantt
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {activeProjects.length > 0 ? (
            <div className="hidden md:flex items-center gap-2">
              <FolderOpen size={14} weight="bold" className="text-neutral-500" />
              <Select value={projectId || ""} onValueChange={setProjectId}>
                <SelectTrigger data-testid="plan-project-selector" className="h-9 min-w-[200px]">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {activeProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="plan-back-btn" className="h-9">
              <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      {/* Title & toolbar */}
      <div className="border-b border-neutral-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-mono px-2 py-1 rounded"
              style={{ background: `linear-gradient(135deg, ${BRAND.colors.blue}, ${BRAND.colors.green})`, color: "white" }}
            >
              Project Plan
            </div>
            <h1
              className="mt-2 text-2xl font-black tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif", color: BRAND.colors.ink }}
            >
              {project ? project.name : "Plan"}
            </h1>
            <p className="text-xs text-neutral-500 mt-0.5">
              {customer ? `${customer.name} · ` : ""}
              {tasks.length} task{tasks.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Zoom */}
            <div className="inline-flex items-center h-9 rounded-md border border-neutral-200 bg-neutral-50 p-0.5">
              {ZOOM_OPTIONS.map((z) => (
                <button
                  key={z}
                  type="button"
                  onClick={() => setZoom(z)}
                  data-testid={`plan-zoom-${z}`}
                  className={`h-8 px-3 rounded text-xs font-semibold capitalize transition-colors ${
                    zoom === z ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-900"
                  }`}
                >
                  {z}
                </button>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleImport}
              data-testid="plan-import-input"
            />
            {/* Critical-path + slack toggles */}
            <button
              type="button"
              onClick={() => setShowCritical((v) => !v)}
              data-testid="plan-critical-toggle"
              className={`h-9 px-3 rounded-md text-xs font-semibold uppercase tracking-widest border transition-colors ${
                showCritical
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
              }`}
              title="Highlight critical path (slack = 0)"
            >
              Critical
            </button>
            <button
              type="button"
              onClick={() => setShowSlack((v) => !v)}
              data-testid="plan-slack-toggle"
              className={`h-9 px-3 rounded-md text-xs font-semibold uppercase tracking-widest border transition-colors ${
                showSlack
                  ? "bg-neutral-900 border-neutral-900 text-white"
                  : "border-neutral-200 text-neutral-500 hover:text-neutral-900"
              }`}
              title="Show slack column in the WBS table"
            >
              Slack
            </button>
            {canEdit ? (
              <Button variant="outline" size="sm" onClick={runReschedule} className="h-9" data-testid="plan-reschedule-btn" title="Recompute CPM schedule">
                Reschedule
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                variant="outline"
                size="sm"
                onClick={runLevelResources}
                disabled={levelingBusy}
                className="h-9"
                data-testid="plan-level-btn"
                title="Shift tasks with slack to resolve resource over-allocations"
              >
                <Stack size={13} weight="bold" className="mr-1.5" />
                {levelingBusy ? "Leveling…" : "Level Resources"}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCalendarOpen(true)}
              className="h-9"
              data-testid="plan-calendar-btn"
              title="Configure weekends & holidays"
            >
              <CalendarIcon size={13} weight="bold" className="mr-1.5" /> Calendar
            </Button>
            {canEdit ? (
              <div className="inline-flex items-center h-9 rounded-md border border-neutral-200 bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={doUndo}
                  disabled={undoStack.length === 0}
                  data-testid="plan-undo-btn"
                  className="h-full px-2.5 text-xs uppercase tracking-widest font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Undo (Cmd/Ctrl+Z)"
                >
                  Undo
                </button>
                <div className="w-px h-5 bg-neutral-200" />
                <button
                  type="button"
                  onClick={doRedo}
                  disabled={redoStack.length === 0}
                  data-testid="plan-redo-btn"
                  className="h-full px-2.5 text-xs uppercase tracking-widest font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Redo (Cmd/Ctrl+Shift+Z)"
                >
                  Redo
                </button>
              </div>
            ) : null}
            {canEdit ? (
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-9" data-testid="plan-import-btn">
                <UploadSimple size={14} weight="bold" className="mr-1.5" /> Import CSV
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => doExport("csv")} className="h-9" data-testid="plan-export-csv-btn">
              <DownloadSimple size={14} weight="bold" className="mr-1.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => doExport("xml")} className="h-9" data-testid="plan-export-xml-btn">
              <DownloadSimple size={14} weight="bold" className="mr-1.5" /> MS Project XML
            </Button>
            {canEdit ? (
              <Button
                size="sm"
                onClick={openNew}
                data-testid="plan-new-btn"
                className="h-9 text-white"
                style={{ backgroundColor: BRAND.colors.blue }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blueDeep)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blue)}
              >
                <Plus size={14} weight="bold" className="mr-1.5" /> New task
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body: tabs + content */}
      {!projectId ? (
        <div className="flex-1 flex items-center justify-center text-sm text-neutral-500">
          Select a project to view its plan.
        </div>
      ) : (
        <>
          <div className="border-b border-neutral-200 bg-white px-6 flex items-center gap-1" data-testid="plan-tabs">
            {[
              { id: "gantt", label: "Gantt", Icon: ChartBar },
              { id: "pert", label: "PERT", Icon: Graph },
              { id: "resources", label: "Resources", Icon: Users },
              { id: "usage", label: "Usage", Icon: ChartBar },
              { id: "baselines", label: "Baselines", Icon: Camera },
              { id: "evm", label: "EVM", Icon: Wallet },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`plan-tab-${id}`}
                className={`inline-flex items-center gap-1.5 h-11 px-4 text-xs uppercase tracking-widest font-semibold border-b-2 transition-colors ${
                  tab === id
                    ? "border-[#387EE6] text-[#0F172A]"
                    : "border-transparent text-neutral-500 hover:text-neutral-900"
                }`}
              >
                <Icon size={13} weight="bold" /> {label}
              </button>
            ))}
          </div>

          {tab === "gantt" || tab === "pert" ? (
            <div className="border-b border-neutral-200 bg-white px-6 py-2 flex items-center gap-3 flex-wrap" data-testid="plan-views-row">
              <ViewsBar
                projectId={projectId}
                canEdit={canEdit}
                currentConfig={currentViewConfig}
                onApply={applyViewConfig}
              />
              <div className="h-6 w-px bg-neutral-200" />
              <div className="inline-flex items-center gap-1.5 text-xs">
                <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Assignee</span>
                <Input
                  value={filterAssignee}
                  onChange={(e) => setFilterAssignee(e.target.value)}
                  placeholder="Filter"
                  className="h-7 w-32 text-xs"
                  data-testid="plan-filter-assignee"
                />
                {filterAssignee ? (
                  <button
                    type="button"
                    onClick={() => setFilterAssignee("")}
                    data-testid="plan-filter-assignee-clear"
                    className="text-neutral-400 hover:text-neutral-700 text-[10px] uppercase"
                  >
                    clear
                  </button>
                ) : null}
              </div>
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideCompleted}
                  onChange={(e) => setHideCompleted(e.target.checked)}
                  data-testid="plan-hide-completed"
                />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Hide 100% complete</span>
              </label>
            </div>
          ) : null}

          {tab === "gantt" ? (
            <div className="flex-1 overflow-hidden flex">
              {/* Left table */}
              <div
                className="border-r border-neutral-200 bg-white flex flex-col overflow-hidden"
                style={{ width: 520, minWidth: 340 }}
                data-testid="plan-table"
              >
                <div className="overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#F8FAFC] border-b border-neutral-200">
                      <tr className="text-[10px] uppercase tracking-[0.15em] text-neutral-500" style={{ height: 48 }}>
                        <th className="px-2 text-left w-16">WBS</th>
                        <th className="px-2 text-left">Name</th>
                        <th className="px-2 text-left">Start</th>
                        <th className="px-2 text-left">Finish</th>
                        <th className="px-2 text-right w-14">Dur</th>
                        {showSlack ? <th className="px-2 text-right w-16">Slack</th> : null}
                        <th className="px-2 text-right w-14">%</th>
                        <th className="px-2 w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="group">
                      {loading && tasks.length === 0 ? (
                        <tr><td colSpan={showSlack ? 8 : 7} className="text-center py-10 text-neutral-400 text-sm">Loading plan…</td></tr>
                      ) : tasks.length === 0 ? (
                        <tr>
                          <td colSpan={showSlack ? 8 : 7} className="text-center py-10">
                            <div className="text-sm text-neutral-500 mb-3">No plan tasks yet.</div>
                            {canEdit ? (
                              <Button size="sm" onClick={openNew} style={{ backgroundColor: BRAND.colors.blue }} className="text-white" data-testid="plan-empty-new-btn">
                                <Plus size={14} weight="bold" className="mr-1.5" /> Create first task
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      ) : visibleTasks.map((t) => (
                        <TaskRow
                          key={t.id}
                          t={t}
                          depth={t._depth || 0}
                          expanded={!collapsed.has(t.id)}
                          hasKids={hasChildrenMap.has(t.id)}
                          isSelected={t.id === selectedId}
                          canEdit={canEdit}
                          showSlack={showSlack}
                          onToggle={handleToggleCollapse}
                          onSelect={(x) => setSelectedId(x.id)}
                          onEdit={openEdit}
                          onDelete={handleDelete}
                          onIndent={handleIndent}
                          onOutdent={handleOutdent}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Right gantt */}
              <GanttChart
                tasks={visibleTasks}
                zoom={zoom}
                selectedId={selectedId}
                showCritical={showCritical}
                onRowClick={(t) => setSelectedId(t.id)}
                onReschedule={canEdit ? handleReschedule : undefined}
                onResize={canEdit ? handleResize : undefined}
                onConnect={canEdit ? handleConnect : undefined}
              />
            </div>
          ) : tab === "pert" ? (
            <PertChart projectId={projectId} />
          ) : tab === "resources" ? (
            <ResourcesTab projectId={projectId} canEdit={canEdit} />
          ) : tab === "usage" ? (
            <UsageTab projectId={projectId} />
          ) : tab === "baselines" ? (
            <BaselinesTab projectId={projectId} canEdit={canEdit} />
          ) : tab === "evm" ? (
            <EvmTab projectId={projectId} />
          ) : null}
        </>
      )}

      <TaskEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editorInitial}
        tasks={tasks}
        resources={resources}
        onSubmit={handleSave}
      />
      <CalendarDialog
        open={calendarOpen}
        onOpenChange={setCalendarOpen}
        projectId={projectId}
        canEdit={canEdit}
        onSaved={loadTasks}
      />
    </div>
  );
}
